import { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { getAuthData } from '../../../../../../services/storage/LocalStorage';
import { RenderCellContent } from './components/RenderCell/renderCellContent';
import FieldGenerator from './components/FieldGenerator';
import { useStyles } from '../../../../../../assets/muiStyles';
import { Divider, SaveButton, StyledIconTableCell, StyledTableCell, StyledTableRow } from './Styled';



const EditRowComponent = ({
    row,
    key = null,
    options,
    onlineStatus,
    selectedRow,
    handleAction,
    onConfirm,
    onDiscard,
    setShowMandatory,
    handleSelectRow,
    onRowInputChange,
    handleInlineFormAction,
    disableSubmit,
    setDisableSubmit,
    setDisableParentSaveButton
}) => {
    const { user: { userTimezone } } = getAuthData();
    const defaultUserTimezone = 'Etc/UTC';

    const [isLookupInvalidSearch, setIsLookupInvalidSearch] = useState(false);
    const [isLookupValueChanged, setIsLookupValueChanged] = useState(false);
    const [isDefaultLookupInvalid, setIsDefaultLookupInvalid] = useState(false);
    const userDomain = useSelector((state) => state?.selectedDomain?.userDomain);
    const addManuallyRow = useSelector((state) => state?.filters?.addManuallyRow);

    const newRowData = Object.values(row) && Object.values(row)[0]?.rowOptions;
    const isNewRow = newRowData?.newRow;
    const isMainOtherMenu = options?.rowMenu?.length > 0;
    const classes = useStyles();

    const disableFormSubmit = (value) => {
        setDisableSubmit(value);
    };

    useEffect(() => {
        if (!disableSubmit && setDisableParentSaveButton) {
            setDisableParentSaveButton(true);
        }
    }, [disableSubmit]);
    

    useEffect(() => {
        const record = Object.values(row)[0];
        if (record?.fields?.length) {
            const hasInvalidLookup = record.fields.some(
                (field) => field?.type === 'lookup' && !field.value
            );

            if (hasInvalidLookup) {
                setIsDefaultLookupInvalid(true);
            }
        }
    }, [row]);


    useEffect(() => {
        setIsLookupInvalidSearch(false);
        if (Object.values(row)[0].fields && Object.values(row)[0].fields.length) {
            const mandatoryFields = [];
            const optionalFields = [];
            let lookupSearchString = "";

            const isEmptyValues = (value) => value === undefined
                || value === null
                || (typeof value === 'object' && Object.keys(value).length === 0)
                || (typeof value === 'string' && value.length === 0);

            Object.values(row)[0].fields.forEach((el, index) => {
                if (el?.type === 'lookup') {
                    lookupSearchString = el?.value?.text;
                    if (!isDefaultLookupInvalid && !isLookupValueChanged) {
                        setIsLookupInvalidSearch(false);
                    } else if (!el.results || !el.results.length) {
                        setIsLookupInvalidSearch(true);
                    } else if (el.results && el.results.length > 0) {
                        if (el.value.text) {
                            const resultsFromSearch = el.results.map(
                                (eachObject) => eachObject.text,
                            );
                            if (resultsFromSearch.includes(el.value.text)) {
                                setIsLookupInvalidSearch(false);
                            } else {
                                setIsLookupInvalidSearch(true);
                            }
                        } else {
                            setIsLookupInvalidSearch(true);
                        }
                    }
                }

                if (el.mandatory && (isEmptyValues(el.value) || el.validationResult === false)) {
                    mandatoryFields.push(el);
                    setShowMandatory(true)
                } else {
                    optionalFields.push(el);
                }
            });

            if (mandatoryFields?.length || (isLookupInvalidSearch && lookupSearchString?.length > 0)
                || optionalFields.some(x => x.validationResult === false)) {
                setDisableSubmit(true);
            } else {
                setDisableSubmit(false);
            }
        }
    }, [row]);


    const handleChange = (e, type, val) => {
        let isInputEdited = false;
        let isLookupValueModified = false;
        const rowKey = Object.keys(row)[0];
        const rowData = row[rowKey];

        const updatedFields = rowData.fields.map((el) => {
            const isCurrentElement = el.id === e?.target?.name;
            if (!isCurrentElement) {
                return el;
            }
            const inputValueChanged = el.value !== e?.target?.value;
            if (inputValueChanged) {
                isInputEdited = true;
            }
            if (el.type === 'lookup') {
                isLookupValueModified = true;
            }

            let shouldUpdateValue = true;
            let newValue = el.value;

            switch (type) {
                case 'lookup':
                    // this is because in validation, its fetched as el.value.text
                    newValue = { text: val };
                    break;
                case 'uploadFile':
                    newValue = val;
                    break;
                case 'removeUploadFile':
                    shouldUpdateValue = false; // Indicate not to include 'value'
                    break;
                default:
                    newValue = typeof e?.target?.value === 'string' ? e?.target?.value.trim() : e?.target?.value;
            }
            const { value, validationResult, validationMessage, ...restEl } = el;
            const updatedEl = shouldUpdateValue ? { ...restEl, value: newValue } : restEl;

            return updatedEl;
        });

        if (isLookupValueModified) {
            setIsLookupValueChanged(true);
        }

        const updatedRow = {
            ...row,
            [rowKey]: {
                ...rowData,
                fields: updatedFields,
            },
        };

        onRowInputChange(e, updatedRow, isInputEdited);
    };

    const handleBlur = (e, action = 'move') => {
        if (addManuallyRow) {
            // Get dynamic key from fieldData
            const dynamicKey = Object.keys(row)[0];
            const schema = row[dynamicKey].fields;

            const errors = [];

            // Iterate through visible fields
            schema.forEach((field) => {
                if (!field.hidden) {
                    const userInput = field.value ?? "";

                    // Check if mandatory and value is missing
                    if (field.mandatory && (!userInput || userInput.trim() === "")) {
                        errors.push(`${field.title} is mandatory.`);
                    }
                }
            });

            // if (errors.length > 0) {
            //     return;
            // }

            setDisableSubmit(false);
        };

        Object.values(row)[0].fields.forEach((el) => {
            let currentFieldValue = '';
            if (el.id === e.target.name) {
                switch (el.type) {
                    case 'lookup':
                    case 'uploadFile':
                        currentFieldValue = e.value || e.target.value;
                        break;
                    default:
                        currentFieldValue = e.target.value;
                        break;
                }
                handleInlineFormAction(
                    action,
                    Object.keys(row)[0],
                    el.id,
                    { currentFieldValue },
                    Object.values(row)[0].rowOptions.newRow,
                );
            }
        });
    };

    const onSelectField = (id, value = '', action = 'move') => {
        const rowKey = Object.keys(row)[0];
        const rowData = row[rowKey];
        const updatedFields = rowData.fields.map((el) => {
            const isIdSame = el.id === id;
            if (!isIdSame) {
                return el;
            }
            const { validationResult, validationMessage, ...restEl } = el;
            return {
                ...restEl,
                value, // Assign the new value directly.
            };
        });
        const updatedRow = {
            ...row,
            [rowKey]: {
                ...rowData,
                fields: updatedFields,
            },
        };

        onRowInputChange(undefined, updatedRow);

        handleInlineFormAction(
            action,
            rowKey,
            id,
            { currentFieldValue: value },
            rowData.rowOptions.newRow,
        );
    };

    const handleChangeSwitch = (e) => {
        const rowKey = Object.keys(row)[0];
        const rowData = row[rowKey];
        const updatedFields = rowData.fields.map((el) => {
            if (el.id !== e.target.name) {
                return el;
            }
            const { validationResult, validationMessage, ...restEl } = el;
            return {
                ...restEl,
                value: e.target.checked,
            };
        });

        const updatedRow = {
            ...row,
            [rowKey]: {
                ...rowData,
                fields: updatedFields,
            },
        };

        onRowInputChange(e, updatedRow);

        handleInlineFormAction(
            'move',
            rowKey,
            e.target.name,
            { currentFieldValue: e.target.checked },
            rowData.rowOptions.newRow,
        );
    };

    const handleChangeCheck = (e) => {
        const rowKey = Object.keys(row)[0];
        const rowData = row[rowKey];

        const updatedFields = rowData.fields.map((el) => {
            if (el.id !== e.target.id) {
                return el;
            }

            const currentValue = Array.isArray(el.value) ? el.value : [];
            const newValue = currentValue.includes(e.target.name)
                ? currentValue.filter((item) => item !== e.target.name)
                : [...currentValue, e.target.name];

            return {
                ...el,
                value: newValue,
            };
        });

        const updatedRow = {
            ...row,
            [rowKey]: {
                ...rowData,
                fields: updatedFields,
            },
        };
        onRowInputChange(e, updatedRow);
        handleInlineFormAction(
            'move',
            rowKey,
            e.target.id,
            { currentFieldValue: updatedFields.find((el) => el.id === e.target.id)?.value },
            rowData.rowOptions.newRow,
        );
    };

    const handleChangeDateTime = (value, name) => {
        const rowKey = Object.keys(row)[0];
        const rowData = row[rowKey];

        const updatedFields = rowData.fields.map((el) => {
            if (el.id !== name) {
                return el;
            }
            const ipochDate = moment(value)
                .tz(userTimezone || defaultUserTimezone)
                .format('x');

            const { validationResult, validationMessage, ...restEl } = el;
            return {
                ...restEl,
                value: parseInt(ipochDate, 10),
            };
        });
        const updatedRow = {
            ...row,
            [rowKey]: {
                ...rowData,
                fields: updatedFields,
            },
        };

        onRowInputChange(undefined, updatedRow);
    };

    const handleChangeData = (value, name) => {
        const rowKey = Object.keys(row)[0];
        const rowData = row[rowKey];

        const updatedFields = rowData.fields.map((el) => {
            if (el.id !== name) {
                return el;
            }
            const ipochDate = moment.utc(value, 'YYYY-MM-DD').unix() * 1000;

            const { validationResult, validationMessage, ...restEl } = el;
            return {
                ...restEl,
                value: parseInt(ipochDate, 10),
            };
        });
        const updatedRow = {
            ...row,
            [rowKey]: {
                ...rowData,
                fields: updatedFields,
            },
        };

        onRowInputChange(undefined, updatedRow);

    };

    const handleBlurDateTime = (value, name) => {
        const rowKey = Object.keys(row)[0];
        const rowData = row[rowKey];
        let epochDate;

        const updatedFields = rowData.fields.map((el) => {
            if (el.id !== name) {
                return el;
            }
            epochDate = moment(value).utc().format('x');
            return {
                ...el,
                value: parseInt(epochDate, 10),
            };
        });

        const updatedRow = {
            ...row,
            [rowKey]: {
                ...rowData,
                fields: updatedFields,
            },
        };

        handleInlineFormAction(
            'move',
            rowKey,
            name,
            { currentFieldValue: parseInt(epochDate, 10) || '' },
            Object.values(updatedRow)[0].rowOptions.newRow,
        );
    };

    const handleBlurDate = (value, name) => {
        const rowKey = Object.keys(row)[0];
        const rowData = row[rowKey];
        let epochDate;

        const updatedFields = rowData.fields.map((el) => {
            if (el.id !== name) {
                return el;
            }
            epochDate = moment.utc(value, 'YYYY-MM-DD').unix() * 1000;
            return {
                ...el,
                value: parseInt(epochDate, 10),
            };
        });

        const updatedRow = {
            ...row,
            [rowKey]: {
                ...rowData,
                fields: updatedFields,
            },
        };

        handleInlineFormAction(
            'move',
            rowKey,
            name,
            { currentFieldValue: parseInt(epochDate, 10) || '' },
            Object.values(updatedRow)[0].rowOptions.newRow,
        );
    };

    const handleClick = (e, type, id, title = null) => {
        setDisableSubmit(false);

        e.persist();
        switch (type) {
            case 'button':
                handleInlineFormAction(
                    'click',
                    Object.keys(row)[0],
                    id,
                    { currentFieldValue: title || '' },
                    Object.values(row)[0].rowOptions.newRow,
                );
                break;
            case 'uploadFile': {
                alert('Remove uploaded file action not supported!');
                break;
            }
            case 'removeUploadFile': {
                alert('Remove uploaded file action not supported!');
                break;
            }
            default:
                break;
        }
    };

    const handleOnFieldAction = () => {
        const rowKey = Object.keys(row)[0];
        const rowData = row[rowKey];
        const rowFormatted = {};

        rowData.fields.forEach((item) => {
            rowFormatted[item.id] = item.value;
        });

        handleAction(
            'onFieldAction',
            { row: rowFormatted },
            null,
            rowKey,
        );
    };


    return (
        <StyledTableRow isOffline={onlineStatus === 0 ? 'true' : 'false'} className='edit-values'>
            <StyledTableCell padding='none' align='left' />

            {options.selectableRows && (
                <StyledTableCell>
                    <Checkbox
                        className={classes.customCheckbox}
                        checked={!!selectedRow?.filter((item) => item[key] === row[key])?.length}
                        onChange={(e) => handleSelectRow(e, row)}
                        color='primary'
                    />
                </StyledTableCell>
            )}

            {isMainOtherMenu && !isNewRow && <StyledTableCell />}
            {!isMainOtherMenu && (options?.tableId == "radioSilence" || options?.tableId == "trackingAreaZones") && <StyledTableCell />}
            {options?.columnNames
                && Object.values(row)[0].fields.map((column) => {
                    if (Object.keys(options.columnNames).includes(column.id)) {
                        if (!column.hidden) {
                            return (
                                <StyledTableCell key={column.id}>
                                    <FieldGenerator
                                        field={column}
                                        options={options}
                                        handleChange={handleChange}
                                        onBlurField={handleBlur}
                                        handleClick={handleClick}
                                        onSelectField={onSelectField}
                                        handleChangeSwitch={handleChangeSwitch}
                                        handleChangeCheck={handleChangeCheck}
                                        handleChangeDateTime={handleChangeDateTime}
                                        handleBlurDateTime={handleBlurDateTime}
                                        handleBlurDate={handleBlurDate}
                                        handleChangeData={handleChangeData}
                                        disableFormSubmit={disableFormSubmit}
                                    />
                                </StyledTableCell>
                            );
                        }

                        return (
                            <StyledTableCell key={column.id}>
                                <RenderCellContent
                                    row={row}
                                    column={column}
                                    userDomain={userDomain}
                                    userTimezone={userTimezone}
                                    defaultUserTimezone={defaultUserTimezone}
                                    handleAction={handleAction}
                                    handleOnFieldAction={handleOnFieldAction}
                                />
                            </StyledTableCell>
                        );
                    }
                    return null;
                })
            }

            <StyledIconTableCell>
                <IconButton
                    aria-label='delete'
                    size='small'
                    onClick={() => onDiscard(row)}
                >
                    <CloseIcon color='#aeb8d6' fontSize='small' />
                </IconButton>
                <Divider />
                <SaveButton
                    variant='contained'
                    disabled={disableSubmit}
                    onClick={() => onConfirm(row)}
                    userDomain={userDomain}
                >
                    Save
                </SaveButton>
            </StyledIconTableCell>
        </StyledTableRow>
    );
};

export default EditRowComponent;
